import { useMemo } from "react";
import { Link } from "react-router-dom";

import PremisesPanel from "../../components/director/dashboard/PremisesPanel";
import ModuleShell from "../../components/director/module1/ModuleShell";
import useDirectorSurface from "../../components/director/module1/useDirectorSurface";
import { ScenariosPanelBody } from "../../components/director/scenarios/ScenariosPanel";
import { useDirectorContext } from "@contexts/director";
import { companyQuery } from "./module1Utils";
import { primaryScreen } from "./complementaryPages";

export const MANAGE_PREMISES_CAPABILITY = "director.cost_plan.write";

const DASHBOARD_QUERY = { scope: "obras" };

/**
 * Premissas e cenários (spec `director/workspace-navigation`): o que o Sienge não sabe, por empresa
 * e por obra, e os parâmetros dos cenários Conservador e Estresse. O contrato de API é o mesmo de
 * antes (`director/premises` com `expectedVersion` e `reason`); os dois painéis só mudaram de
 * lugar. Sem a permissão de gerir premissas a página é só leitura e diz a quem pedir.
 */
const PremisesPage = () => {
  const { selectedCompanyId, accessByCompany = {}, metadata } = useDirectorContext();
  const access = accessByCompany[selectedCompanyId] || {};
  const canManage = (access.capabilities || []).includes(MANAGE_PREMISES_CAPABILITY);
  // A lista de obras (com a carteira de cada uma) vem da Visão geral; enquanto ela não chega,
  // as obras do filtro da Diretoria já deixam o formulário montado.
  const surface = useDirectorSurface("dashboard", DASHBOARD_QUERY);
  const metadataObras = useMemo(() => (metadata?.projects || [])
    .map((project) => ({ projectKey: project.key, name: project.name, paymentTermMix: null })), [metadata]);
  const obras = surface.data?.obras ?? (metadataObras.length ? metadataObras : null);
  const dashboard = primaryScreen("dashboard");
  const query = companyQuery(selectedCompanyId);

  return <ModuleShell
    title="Premissas e cenários"
    description="Premissas usadas nos indicadores da Visão geral e nos cenários do fluxo."
    showProjectFilter={false}
  >
    {!canManage && <p className="director-workspace__warning" role="note">
      Você pode consultar as premissas em vigor, mas não alterá-las. Para mudar algum valor, peça a quem gere as
      premissas da Diretoria da {access.companyName || "empresa"}: a alteração exige a permissão de gerir premissas,
      liberada pelo administrador em Backoffice › Gestão de Produtos.
    </p>}
    <section className="director-section director-dashboard-premises" aria-label="Premissas do diretor">
      <header>
        <div>
          <span className="director-eyebrow">Empresa e obras</span>
          <h2>Premissas</h2>
        </div>
        <p>
          O que o Sienge não sabe: taxa de desconto, impostos e, por obra, o valor das unidades a vender, o ritmo,
          o início das vendas, a condição de pagamento e o terreno fora do Sienge.
        </p>
      </header>
      <PremisesPanel
        companyId={selectedCompanyId}
        obras={obras}
        onSaved={surface.reload}
        readOnly={!canManage}
        readOnlyHint={canManage ? undefined : null}
      />
    </section>
    <section className="director-section director-scenarios__panel" aria-label="Parâmetros dos cenários">
      <header>
        <div>
          <span className="director-eyebrow">Cenários</span>
          <h2>Conservador e Estresse</h2>
        </div>
        <p>Atraso e corte das entradas futuras e acréscimo das saídas ainda não comprometidas.</p>
      </header>
      <ScenariosPanelBody
        key={selectedCompanyId ?? ""}
        companyId={selectedCompanyId}
        readOnly={!canManage}
        readOnlyHint={canManage ? undefined : null}
      />
    </section>
    <p className="director-owner-footnote">
      <Link to={`${dashboard.path}${query}`}>Ver a Visão geral com as premissas em vigor</Link>
    </p>
  </ModuleShell>;
};

export default PremisesPage;
